
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  Renderer2,
  ViewChild,
  OnInit,
  AfterViewInit,
  AfterViewChecked,
  DOCUMENT,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  standalone: false,
  selector: 'chat-bot-query',
  templateUrl: './chat-bot-query.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chat-bot-query.component.scss',
})
export class ChatBotQueryComponent
  implements OnInit, AfterViewInit, AfterViewChecked
{
  @ViewChild('autoCompleteInput', { static: false })
  autoCompleteInput!: ElementRef;
  @Input() suggestionList: any = [];
  @Output() QuestionComplete = new EventEmitter<{
    data: string;
    action: string;
    tags?: string[];
  }>();
  @Input() autoSearchDafaultValue: string[] = [];
  @Input() defaultSelectLanguage: any;
  @Input() botType: any;
  @Input() suggestionEnable: any;
  @Input() allowClear!: boolean;
  singleTag: string = '';
  showSearches: boolean = false;
  isSearching: boolean = false;
  searchedList: any = [];
  selectedTags: string[] = [];
  @Input() placeholderText: any = 'Ask Anything...';
  constructor(
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.searchedList = this.suggestionList;

    this.renderer.listen('window', 'click', (e: any) => {
      /**
       * only run when togglebutton is not clicked
       * if we don't check this, all clicks (even on the toggle button) gets into this
       * section which in the result we might never see the menu open!
       * and the menu itself is checked here, and it's where we check just outside of
       * the menu and button the condition abbove must close the menu
       */
      if (e.target !== this.autoCompleteInput.nativeElement) {
        this.showSearches = false;
      }
    });
  }

  ngOnInit() {
    this.showSearches = false;
    setTimeout(() => {
      this.Search();
    }, 2000);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.autoCompleteInput.nativeElement.focus();
    }, 2000);
  }
  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  getList(name: any): Observable<any> {
    return of(this.filterList(name));
  }

  filterList(name: any) {
    return this.suggestionList
      .filter((val: any) =>
        val['question']
          .trim()
          .toLowerCase()
          .includes(name.trim().toLowerCase()),
      )
      .map((data: any) => data['question']);
  }

  Search() {
    const search$ = fromEvent(
      this.autoCompleteInput.nativeElement,
      'keyup',
    ).pipe(
      // map((event: any) => this.autoSearchDafaultValue = event.target.value),
      map((event: any) => (this.singleTag = event.target.value)),
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isSearching = true)),
      switchMap((term) =>
        term ? this.getList(term) : of<any>(this.suggestionList),
      ),
      tap(() => {
        this.isSearching = false;
        this.showSearches = true;
      }),
    );

    search$.subscribe((data) => {
      this.isSearching = false;
      if (this.singleTag) {
        this.searchedList = data;
      } else {
        this.searchedList = data.map((data: any) => data['question']);
      }
    });
  }

  setSelected(data: string) {
    // Add to selected tags if not already present
    if (data && !this.selectedTags.includes(data)) {
      this.selectedTags.push(data);
    }
    this.searchedList = this.filterList(data);
    this.autoCompleteInput.nativeElement.value = '';
    this.singleTag = '';
    this.showSearches = false;
    // Keep focus on input for creating next tag
    setTimeout(() => {
      if (this.autoCompleteInput?.nativeElement) {
        this.autoCompleteInput.nativeElement.focus();
      }
    }, 0);
  }

  removeTag(tag: string) {
    const index = this.selectedTags.indexOf(tag);
    if (index > -1) {
      this.selectedTags.splice(index, 1);
    }
  }

  trackById(index: any, item: any): void {
    return item._id;
  }

  closeDropDown(): void {
    this.showSearches = false;
  }

  onEnterKeyDown(event: Event) {
    // Prevent default Enter behavior (newline in textarea)
    event.preventDefault();

    const trimmedValue = this.singleTag?.trim();

    // Create tag if there's text and it's not already in tags
    if (
      trimmedValue &&
      trimmedValue.length > 0 &&
      !this.selectedTags.includes(trimmedValue)
    ) {
      this.selectedTags.push(trimmedValue);
    }

    // Clear the input field after Enter key press
    // this.autoSearchDafaultValue = '';
    this.singleTag = '';
    if (this.autoCompleteInput?.nativeElement) {
      this.autoCompleteInput.nativeElement.value = '';
    }

    // Force change detection to update ngModel
    this.cdr.detectChanges();

    // Keep focus on input for creating next tag
    setTimeout(() => {
      if (this.autoCompleteInput?.nativeElement) {
        this.autoCompleteInput.nativeElement.focus();
      }
    }, 0);

    this.showSearches = false;
  }

  onKeypressEvent(event: any) {
    this.singleTag = event.target.value;

    if (this.selectedTags.length > 0) {
      this.QuestionComplete.emit({
        data: this.singleTag,
        action: 'Keypress',
        tags: this.selectedTags,
      });
      this.clear();
    }
  }

  clear() {
    this.singleTag = '';
    this.selectedTags = [];
  }

  sendSearch() {
    this.QuestionComplete.emit({
      data: this.singleTag,
      action: 'Keypress',
      tags: this.selectedTags,
    });
    if (this.allowClear && this.selectedTags.length > 0) {
      this.clear();
    }
  }
}
